/**
 * Glue between logged meals and the TEF engine (@engine/tef).
 *
 * Meal carbs are TOTAL carbohydrate (US-label style), so compositions are built with
 * foodFromUsLabel: fiber and sugar alcohols come out of the carb total instead of being counted twice.
 */
import {
  DEFAULT_TEF_PROFILE, ageBracketFor, calculateTef, foodComposition, foodFromUsLabel,
  type EvidenceMode, type FoodComposition, type MealProcessingId, type MetabolicHealthId, type TefResult, type TrainingStatusId,
} from '@engine/tef';
import { ageOn } from './dates';
import type { MealLog, Profile } from './types';

const SETTINGS_KEY: string = 'mightypeanut.tef';

/** The choices the user makes on the TEF page. Age comes from the profile, not from here. */
export interface TefSettings {
  training: TrainingStatusId;
  metabolicHealth: MetabolicHealthId;
  mealProcessing: MealProcessingId;
  mode: EvidenceMode;
}

export const DEFAULT_TEF_SETTINGS: TefSettings = {
  training: DEFAULT_TEF_PROFILE.training,
  metabolicHealth: DEFAULT_TEF_PROFILE.metabolicHealth,
  mealProcessing: 'TYPICAL_OR_PROCESSED',
  mode: 'STRICT',
};

const pick = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T => (allowed.includes(v as T) ? (v as T) : fallback);

/** TEF choices from diet_settings.tef_profile (or any loose object); unknown values become the defaults. */
export function tefSettingsFrom(v: Record<string, unknown> | null | undefined): TefSettings {
  const o: Record<string, unknown> = v ?? {};
  return {
    training: pick(o.training, ['UNTRAINED', 'TRAINED'] as const, DEFAULT_TEF_SETTINGS.training),
    metabolicHealth: pick(o.metabolicHealth, ['NORMAL', 'INSULIN_RESISTANT'] as const, DEFAULT_TEF_SETTINGS.metabolicHealth),
    mealProcessing: pick(o.mealProcessing, ['TYPICAL_OR_PROCESSED', 'WHOLE_FOOD'] as const, DEFAULT_TEF_SETTINGS.mealProcessing),
    mode: pick(o.mode, ['STRICT', 'EXPLORATORY'] as const, DEFAULT_TEF_SETTINGS.mode),
  };
}

/** This browser's saved choices, from before they were synced in diet_settings. */
export function readTefSettings(): TefSettings {
  try {
    const raw: string | null = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_TEF_SETTINGS;
    return tefSettingsFrom(JSON.parse(raw) as Record<string, unknown>);
  } catch {
    return DEFAULT_TEF_SETTINGS;
  }
}

export function saveTefSettings(s: TefSettings): void {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch { /* storage unavailable */ }
}

/**
 * Share of energy spent digesting a mixed diet when a meal carries no macros to work from:
 * about 10% of energy intake. Westerterp KR. Diet induced thermogenesis. Nutr Metab (Lond).
 * 2004;1(1):5. doi:10.1186/1743-7075-1-5.
 */
export const MIXED_DIET_TEF_SHARE: number = 0.10;
/** Range for the same, as a share of intake (Westerterp 2004: 5-15%). */
export const MIXED_DIET_TEF_RANGE = { low: 0.05, high: 0.15 } as const;

/** Macro grams of one meal (or one day's totals) as the TEF helpers read them. */
export interface TefMacros {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number | null;
  alcohol_g: number | null;
  sugar_alcohols_g: number | null;
}

export const tefMacrosOfMeal = (m: Pick<MealLog, 'calories' | 'protein_g' | 'carbs_g' | 'fat_g' | 'fiber_g' | 'alcohol_g' | 'nutrients'>): TefMacros => ({
  calories: m.calories, protein_g: m.protein_g, carbs_g: m.carbs_g, fat_g: m.fat_g, fiber_g: m.fiber_g, alcohol_g: m.alcohol_g,
  sugar_alcohols_g: m.nutrients.sugar_alcohols_g ?? null,
});

/**
 * Energy spent digesting a list of meals (central personal estimate, kcal). Each meal with macros
 * goes through the TEF engine (the US-label way: fiber and sugar alcohols come out of the carb
 * total); a meal with calories but no macros is counted at the mixed-diet 10%. The engine is
 * linear in grams, so this equals one calculation on the day's summed composition.
 */
export interface MealsTef {
  /** Central estimate, kcal. */
  kcal: number;
  low: number;
  high: number;
  /** Part worked out from macros by the TEF engine. */
  fromMacrosKcal: number;
  /** Part estimated at 10% for meals logged without macros. */
  estimatedKcal: number;
  /** Calories of the meals logged without macros. */
  noMacroCalories: number;
}

export function tefOfMeals(meals: ReadonlyArray<TefMacros>, settings: TefSettings, ageYears: number | null): MealsTef {
  let fromMacros: number = 0;
  let low: number = 0;
  let high: number = 0;
  let estimated: number = 0;
  let noMacroCalories: number = 0;
  const options = {
    profile: { age: ageBracketFor(ageYears), training: settings.training, metabolicHealth: settings.metabolicHealth },
    mealProcessing: settings.mealProcessing,
    mode: settings.mode,
  };
  for (const m of meals) {
    const hasMacros: boolean = g(m.protein_g) + g(m.carbs_g) + g(m.fat_g) + g(m.alcohol_g) > 0;
    if (hasMacros) {
      const food: FoodComposition = foodFromUsLabel({
        proteinG: g(m.protein_g), totalCarbG: g(m.carbs_g), fatG: g(m.fat_g),
        fiberG: g(m.fiber_g), sugarAlcoholG: g(m.sugar_alcohols_g), alcoholG: g(m.alcohol_g),
      });
      const r = calculateTef(food, options).personalTefKcal;
      fromMacros += r.mid;
      low += r.low;
      high += r.high;
    } else if (g(m.calories) > 0) {
      estimated += g(m.calories) * MIXED_DIET_TEF_SHARE;
      low += g(m.calories) * MIXED_DIET_TEF_RANGE.low;
      high += g(m.calories) * MIXED_DIET_TEF_RANGE.high;
      noMacroCalories += g(m.calories);
    }
  }
  return { kcal: fromMacros + estimated, low, high, fromMacrosKcal: fromMacros, estimatedKcal: estimated, noMacroCalories };
}

const g = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);

/** One meal as the engine sees it. Sugar alcohols come from the full nutrient map when a food reported them. */
export function mealComposition(meal: Pick<MealLog, 'protein_g' | 'carbs_g' | 'fat_g' | 'fiber_g' | 'alcohol_g' | 'nutrients'>): FoodComposition {
  return foodFromUsLabel({
    proteinG: g(meal.protein_g),
    totalCarbG: g(meal.carbs_g),
    fatG: g(meal.fat_g),
    fiberG: g(meal.fiber_g),
    sugarAlcoholG: g(meal.nutrients.sugar_alcohols_g),
    alcoholG: g(meal.alcohol_g),
  });
}

/** Sum of meals, each converted first so one meal's clamping never borrows another meal's carbs. */
export function dayComposition(meals: ReadonlyArray<MealLog>): FoodComposition {
  let p = 0, c = 0, fi = 0, sa = 0, f = 0, a = 0;
  for (const m of meals) {
    const x: FoodComposition = mealComposition(m);
    p += x.proteinG; c += x.availableCarbG; fi += x.fiberG; sa += x.sugarAlcoholG; f += x.fatG; a += x.alcoholG;
  }
  return foodComposition({ proteinG: p, availableCarbG: c, fiberG: fi, sugarAlcoholG: sa, fatG: f, alcoholG: a });
}

/** TEF for a composition with the user's settings; age bracket from the profile's birth date on `day`. */
export function tefFor(food: FoodComposition, settings: TefSettings, profile: Pick<Profile, 'birth_date'>, day: string): TefResult {
  const age: number | null = profile.birth_date ? ageOn(profile.birth_date, day) : null;
  return calculateTef(food, {
    profile: { age: ageBracketFor(age), training: settings.training, metabolicHealth: settings.metabolicHealth },
    mealProcessing: settings.mealProcessing,
    mode: settings.mode,
  });
}
